<template>
  <div class="loader-container" :class="{ 'fade-out': !isLoading }">
    <div class="loader">
      <img src="@/assets/images/logo.png" alt="Fix Glass Logo" class="loader-logo" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';

const isLoading = ref(true);
const mountedAt = Date.now();
const MIN_DISPLAY_MS = 500; // evitar flash en cargas rápidas
const MAX_DISPLAY_MS = 3000; // tope: nunca más de 3s de logo
let loadFired = document.readyState === 'complete';
let readyFired = false;
let hideScheduled = false;
let forced = false;

const hideLoader = () => {
  if (hideScheduled) return;
  hideScheduled = true;
  nextTick(() => {
    requestAnimationFrame(() => {
      isLoading.value = false;
    });
  });
};

const maybeHide = () => {
  if (hideScheduled || forced) return;
  // Mostrar al menos ~500ms para no hacer un flash en cargas rápidas
  if (Date.now() - mountedAt < MIN_DISPLAY_MS) return;

  // Se oculta cuando window load O cuando la página avisa que su contenido
  // principal ya está listo (Home al pintar, ProductDetails al llegar el
  // producto, etc.). Las páginas que no avisan esperan window load.
  if (loadFired || readyFired) {
    hideLoader();
  }
};

const handleWindowLoad = () => {
  loadFired = true;
  maybeHide();
};

const handleContentReady = () => {
  readyFired = true;
  maybeHide();
};

onMounted(() => {
  if (document.readyState !== 'complete') {
    window.addEventListener('load', handleWindowLoad, { once: true });
  }
  window.addEventListener('app:initial-data-ready', handleContentReady);

  // Chequear por si ya estaba todo listo (readyState complete)
  maybeHide();

  // Tope de seguridad: nunca dejar el loader más de 3s
  window.setTimeout(() => {
    forced = true;
    hideLoader();
  }, MAX_DISPLAY_MS);
});

onBeforeUnmount(() => {
  window.removeEventListener('load', handleWindowLoad);
  window.removeEventListener('app:initial-data-ready', handleContentReady);
});
</script>

<script>
  export default{
    name: 'LoaderAnimation',
  }
</script>

<style scoped>
.loader-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  background: var(--black-color-2);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  transition: opacity 0.5s, visibility 0.5s;
}

.fade-out {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}

.loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
}

.loader-logo {
  width: 200px;
  height: auto;
  animation: pulse 1s infinite;
}


.progress {
  width: 100%;
  height: 100%;
  background: var(--primary-color);
  animation: loading 1s ease infinite;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

</style>
